/**
 * Fiche chantier du distributeur — écran repris de la version du client (itération 31).
 *
 * Ce que cet écran NE montre pas est aussi important que ce qu'il montre. Un
 * distributeur fournit du matériel et en assure le SAV : il n'a aucune raison de
 * connaître le nom, l'adresse, le téléphone, l'e-mail ni les revenus du client final.
 * Le serveur ne les sert pas, et l'écran ne les demande pas.
 *
 * Il permet en revanche ce qui relève du métier : corriger un numéro de série avec sa
 * trace, ajouter un équipement au chantier, et consigner une interaction.
 *
 * Seuls les chemins d'import changent par rapport à son fichier.
 *
 * Lot L1 (26/10) : les interactions visent l'`id` du client RENDU PAR LA FICHE et non le
 * paramètre d'URL (`PanneauInteractions`), les photos s'ouvrent dans la page avec leur
 * date d'ajout (`GaleriePhotos`), et le bloc « Photos de l'installation » s'ajoute
 * (`PhotosInstallation`). Le fichier, passé à 615 lignes, est découpé par responsabilité
 * dans `features/distributeur/components/`.
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthContext';
import { Card, CardContent } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import {
  ArrowLeft, Camera, Sun, Flame, Droplets, BatteryCharging, Thermometer, Radio, Package, Zap,
} from 'lucide-react';
import { fmtDateShort } from '@/features/distributeur/dates';
import { chantiersProposes } from '@/features/distributeur/refus';
import AjoutActifChantier from '@/features/distributeur/components/AjoutActifChantier';
import ChampNumeroSerie from '@/features/distributeur/components/ChampNumeroSerie';
import ChantierPropose from '@/features/distributeur/components/ChantierPropose';
import GaleriePhotos from '@/features/distributeur/components/GaleriePhotos';
import PanneauInteractions from '@/features/distributeur/components/PanneauInteractions';
import PhotosInstallation from '@/features/distributeur/components/PhotosInstallation';

const TYPE_META = {
  PAC: { label: 'Pompe à chaleur', Icon: Flame, color: 'text-orange-500 bg-orange-50' },
  PV: { label: 'Panneaux solaires', Icon: Sun, color: 'text-amber-500 bg-amber-50' },
  Ballon: { label: 'Ballon ECS', Icon: Droplets, color: 'text-cyan-500 bg-cyan-50' },
  Batterie: { label: 'Batterie', Icon: BatteryCharging, color: 'text-violet-500 bg-violet-50' },
  Radiateur: { label: 'Radiateur électrique', Icon: Thermometer, color: 'text-rose-500 bg-rose-50' },
  'Radiateur standard': { label: 'Radiateur standard', Icon: Thermometer, color: 'text-rose-500 bg-rose-50' },
  'Radiateur R2': { label: 'Radiateur R2', Icon: Radio, color: 'text-rose-600 bg-rose-50' },
  Autre: { label: 'Autre', Icon: Package, color: 'text-neutral-500 bg-neutral-100' },
};

/**
 * Les interactions du client de la fiche : `{ liste, indisponible }`.
 *
 * Toujours lues avec l'`id` RENDU PAR LA FICHE (R1) : sur la route chantier, le
 * paramètre d'URL est une référence de chantier, pas un client. Une fiche sans client
 * n'a pas d'historique à lire ; une lecture en échec ne masque pas la fiche, mais elle
 * est DITE (tour 2) : afficher « Aucune interaction » ferait re-saisir un suivi existant.
 */
const lireInteractions = async (idClient) => {
  if (!idClient) return { liste: [], indisponible: false };
  try {
    const r = await api.get(`/distributeur/clients/${encodeURIComponent(idClient)}/interactions`);
    return { liste: r.data || [], indisponible: false };
  } catch { return { liste: [], indisponible: true }; }
};

export default function DistributorClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isChantierRoute = location.pathname.includes('/sav/chantier/');
  const { user } = useAuth();

  const [client, setClient] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [historiqueIndisponible, setHistoriqueIndisponible] = useState(false);
  const idClientFiche = client?.id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    // ⚠️ ON EFFACE LE REFUS PRÉCÉDENT AVANT DE RECHARGER, ET C'EST CE QUI MANQUAIT.
    //
    // Mesuré en recette sur la préproduction le 09/09/2026. Le refus d'une référence
    // ambiguë propose désormais les chantiers concernés, cliquables. Le clic changeait
    // bien l'adresse, la fiche se chargeait bien — et l'écran restait sur le refus,
    // parce que `error` n'était jamais remis à zéro : le rendu teste `error || !client`
    // et `error` gagnait toujours. Les liens ne menaient donc visuellement nulle part,
    // et le refus redevenait le mur qu'il devait cesser d'être.
    //
    // Aucun test d'API ne pouvait le voir : le serveur répondait juste, les deux fois.
    setError(null);
    setLoading(true);
    try {
      // Route par chantier ou par client-id : la fiche d'abord, puis l'historique du
      // client QU'ELLE désigne, quelle que soit la route d'ouverture.
      const c = await api.get(isChantierRoute
        ? `/distributeur/chantier/${encodeURIComponent(clientId)}`
        : `/distributeur/clients/${encodeURIComponent(clientId)}`);
      setClient(c.data);
      const historique = await lireInteractions(c.data?.id);
      setInteractions(historique.liste);
      setHistoriqueIndisponible(historique.indisponible);
    } catch (e) {
      // On garde le detail ENTIER, objet compris : c'est lui qui porte la liste des
      // chantiers a proposer quand la reference est ambigue.
      setError(e.response?.data?.detail || 'Impossible de charger la fiche');
    }
    setLoading(false);
  }, [clientId, isChantierRoute]);

  useEffect(() => { load(); }, [load]);

  // ⚠️ APRÈS UN AJOUT OU UNE SUPPRESSION, ON NE RELIT QUE L'HISTORIQUE (tour 2, relecture
  // ux). Repasser par `load` remettait le spinner à la place de TOUTE la fiche : la page
  // sautait en haut, le focus clavier se perdait et jusqu'à 12 photos d'installation
  // étaient re-téléchargées pour une ligne de commentaire.
  const rechargerInteractions = useCallback(async () => {
    const historique = await lireInteractions(idClientFiche);
    setInteractions(historique.liste);
    setHistoriqueIndisponible(historique.indisponible);
  }, [idClientFiche]);

  if (loading) return (
    <div className="p-6 flex items-center justify-center h-96">
      <div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
    </div>
  );
  if (error || !client) {
    const propositions = chantiersProposes(error);
    return (
      <div className="p-6 space-y-4" data-testid="client-detail-error">
        <Button variant="outline" size="sm" onClick={() => navigate('/sav')}><ArrowLeft className="w-4 h-4 mr-1" /> SAV</Button>
        <Card><CardContent className="p-6 space-y-4">
          <p className="text-center text-neutral-600" data-testid="client-detail-error-message">
            {(typeof error === 'object' ? error?.message : error) || 'Client introuvable'}
          </p>
          {propositions && (
            <div className="space-y-2" data-testid="chantiers-homonymes">
              {propositions.map((c, rang) => (
                <ChantierPropose key={c.reference_unique || `sans-identifiant-${rang}`} chantier={c} />
              ))}
            </div>
          )}
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto" data-testid="distributor-client-detail">
      {/* Header */}
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/sav')} className="mb-3" data-testid="back-to-sav">
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour SAV
        </Button>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-neutral-900" data-testid="chantier-title">
                Chantier {client.reference_affichee || client.reference_chantier
                  || client.assets?.[0]?.reference_chantier || `#${(clientId || '').slice(0, 8)}`}
              </h1>
            </div>
            <p className="text-sm text-neutral-500 mt-0.5">
              Fiche technique du chantier.
            </p>
          </div>
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-wider text-neutral-400">Équipements installés</p>
            <p className="text-2xl font-semibold text-neutral-900" data-testid="client-assets-count">{client.assets?.length || 0}</p>
            {(client.reference_unique || client.reference_chantier
              || client.assets?.[0]?.reference_chantier) && (
              <AjoutActifChantier
                /* L'identifiant d'abord : appelee avec une reference libre partagee par
                   deux chantiers, la route rattachait l'equipement a l'un des deux au
                   hasard, sans erreur ni trace. Elle refuse desormais — mais le bon
                   geste est de ne pas la mettre dans ce cas. */
                refChantier={client.reference_unique || client.reference_chantier
                  || client.assets[0].reference_chantier}
                onAdded={load}
              />
            )}
          </div>
        </div>
      </div>

      {/* Assets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="client-assets-grid">
        {(client.assets || []).map((a) => {
          const meta = TYPE_META[a.type] || TYPE_META.Autre;
          const Icon = meta.Icon;
          return (
            <Card key={a.id} className="border-neutral-200/80" data-testid={`asset-card-${a.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className={`inline-flex items-center gap-1.5 text-[12px] px-2 py-1 rounded-md font-medium ${meta.color}`}>
                    <Icon className="w-4 h-4" />
                    {a.type}
                    {a.technologie && <span className="text-[10px] opacity-75">· {a.technologie === 'r2' ? 'R2' : a.technologie}</span>}
                    {a.contrat_oa && <span className="text-[9px] bg-emerald-100 text-emerald-800 px-1 rounded ml-1">OA</span>}
                  </div>
                  <span className="text-[10.5px] text-neutral-400">{fmtDateShort(a.date_installation)}</span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-[12px]">
                  <InfoRow label="Marque" value={a.marque} />
                  <InfoRow label="Modèle" value={a.modele} />
                  <ChampNumeroSerie label="N° série" value={a.identifiant_equipement} champ="identifiant_equipement" assetId={a.id} onUpdated={load} tid={`serial-main-${a.id}`} />
                  <InfoRow label="Référence" value={a.reference} />
                  {a.type === 'PV' && (
                    <>
                      <InfoRow label="Type onduleur" value={a.type_onduleur} />
                      <ChampNumeroSerie label="ID centrale" value={a.onduleur_id} champ="onduleur_id" assetId={a.id} onUpdated={load} tid={`serial-plant-${a.id}`} />
                      <InfoRow label="Marque onduleur" value={a.onduleur_marque} />
                      <InfoRow label="Modèle micro-onduleur" value={a.onduleur_modele} />
                      <ChampNumeroSerie label="N° série passerelle / onduleur" value={a.onduleur_serial} champ="onduleur_serial" assetId={a.id} onUpdated={load} tid={`serial-onduleur-${a.id}`} />
                      <InfoRow label="Micro-onduleurs" value={a.nombre_panneaux ? `${a.nombre_panneaux} × ${a.puissance_panneau_wc || '?'} Wc` : null} />
                    </>
                  )}
                  {a.type === 'Ballon' && (
                    <InfoRow label="Nombre de ballons" value={a.nombre_ballons} />
                  )}
                  {a.type === 'Radiateur' && (
                    <InfoRow label="Nombre de radiateurs" value={a.nombre_radiateurs} />
                  )}
                  {a.type === 'PAC' && (
                    <>
                      <InfoRow label="Réf. thermostat" value={a.thermostat_reference} />
                      <ChampNumeroSerie label="N° série thermostat" value={a.thermostat_serial} champ="thermostat_serial" assetId={a.id} onUpdated={load} tid={`serial-thermostat-${a.id}`} />
                    </>
                  )}
                  <InfoRow label="Réf. chantier" value={a.reference_chantier} />
                </div>

                {/* Puissance / Production */}
                {(a.puissance_kw || a.production_annuelle_kwh) && (
                  <div className="flex items-center gap-3 pt-2 border-t border-neutral-100">
                    {a.puissance_kw && (
                      <div className="flex items-center gap-1 text-[11.5px]">
                        <Zap className="w-3.5 h-3.5 text-amber-500" />
                        <span className="font-medium">{a.puissance_kw} {a.type === 'PV' ? 'kWc' : a.type === 'Batterie' ? 'kWh' : 'kW'}</span>
                      </div>
                    )}
                    {a.production_annuelle_kwh && (
                      <div className="text-[11.5px] text-emerald-700 font-medium">
                        {Math.round(a.production_annuelle_kwh)} kWh/an (Hoymiles)
                      </div>
                    )}
                  </div>
                )}

                {/* Photos étiquettes */}
                {a.photos_serial?.length > 0 && (
                  <div className="pt-2 border-t border-neutral-100">
                    <p className="text-[10.5px] uppercase tracking-wide text-neutral-400 font-semibold flex items-center gap-1 mb-2">
                      <Camera className="w-3 h-3" /> Photos étiquettes N° série ({a.photos_serial.length})
                    </p>
                    <GaleriePhotos photos={a.photos_serial.map((p, idx) => ({
                      src: p.data_uri,
                      libelle: p.label || 'Étiquette',
                      taken_at: p.taken_at,
                      testid: `photo-${a.id}-${idx}`,
                    }))} />
                  </div>
                )}
                {(!a.photos_serial || a.photos_serial.length === 0) && (
                  <p className="text-[10.5px] text-neutral-400 italic pt-2 border-t border-neutral-100" data-testid={`no-photos-${a.id}`}>
                    Aucune photo d&apos;étiquette N° série disponible pour cet équipement (installé avant l&apos;obligation photos).
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
        {(!client.assets || client.assets.length === 0) && (
          <Card className="lg:col-span-2"><CardContent className="p-6 text-center text-neutral-400 text-[13px]" data-testid="assets-empty">
            Aucun équipement dans votre périmètre pour ce client.
          </CardContent></Card>
        )}
      </div>

      <PhotosInstallation photos={client.photos_installation} />

      <PanneauInteractions
        clientId={client.id}
        interactions={interactions}
        historiqueIndisponible={historiqueIndisponible}
        user={user}
        onChange={rechargerInteractions}
      />
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-neutral-400 font-medium">{label}</p>
      <p className={`text-neutral-800 ${mono ? 'font-mono text-[11.5px]' : ''}`}>{value}</p>
    </div>
  );
}

